import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, Form, InputGroup } from "react-bootstrap";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormCheck, FormInput } from "go-form";
import useFlash from "go-alert/AlertMessage";
import ImageColorForm from "go-app/components/ImageForm/ImageColorForm";
import handleError from "go-app/services/errors";
import { parseApiData } from "go-core/api/parseApiData";
import { FormSelectGroup } from "go-form/components/FormSelect";
import {
	PrintoutAdditionsPrintMethod,
	ReceiptApi,
} from "../../../../../../../../../../../services/Api/Organization/types";

interface Props {
	receipt: ReceiptApi;
	form: UseFormReturn<ReceiptApi>;
	setNewAvatar: React.Dispatch<React.SetStateAction<string | undefined>>;
	setImageFetched: React.Dispatch<React.SetStateAction<boolean>>;
}

const ReceiptForm = ({ receipt, form, setNewAvatar, setImageFetched }: Props): JSX.Element => {
	const [receiptImageData, setReceiptImageData] = useState<{ default_link: string } | undefined>(undefined);
	const { t } = useTranslation();
	const { addFlash } = useFlash();
	const defaultLuminanceThreshold = receipt?.luminance_threshold ?? 127.5;
	const {
		register,
		formState: { errors },
		setValue,
	} = form;

	const languageOptions = [
		{
			value: "PL",
			label: t("enums.languages.pl", { ns: "lib" }).toLowerCase(),
		},
		{
			value: "ENG",
			label: t("enums.languages.en", { ns: "lib" }).toLowerCase(),
		},
		{
			value: "SPA",
			label: t("enums.languages.es", { ns: "lib" }).toLowerCase(),
		},
	];

	useEffect(() => {
		const getImage = async () => {
			if (!receipt?.logo_link?.default_link) return;
			setImageFetched(false);
			try {
				const downloadedImageAsBlob: Blob = await parseApiData(
					axios.get(receipt.logo_link.default_link, {
						headers: {
							"Access-Control-Allow-Origin": "*",
							"Cache-Control": "no-cache",
						},
						responseType: "blob",
					})
				);
				const imageLinkObj = URL.createObjectURL(downloadedImageAsBlob);
				setReceiptImageData({ default_link: imageLinkObj });
			} catch (err) {
				handleError.alert(err, addFlash);
			}
			setImageFetched(true);
		};

		getImage();
	}, [addFlash, receipt.logo_link?.default_link]);

	const handleImageChange = (imageBase?: string, type?: string) => {
		if (type) {
			setNewAvatar(`data:${type};base64,${imageBase}`);
		}
		if (!type && receiptImageData) {
			setNewAvatar(undefined);
			setReceiptImageData(undefined);
		}
	};

	const onChangeInsta = (evt: React.ChangeEvent<HTMLInputElement>) => {
		if ("inputType" in evt.nativeEvent && evt.nativeEvent.inputType === "insertFromPaste") {
			const parts = evt.target.value.split("/");
			const name = parts[parts.length - 1];
			if (name) {
				setValue("instagram", name);
			}
			if (!name && parts.length >= 2) {
				setValue("instagram", parts[parts.length - 2]);
			}
		} else {
			setValue("instagram", evt.target.value, { shouldDirty: true });
		}
	};

	const onChangeFb = (evt: React.ChangeEvent<HTMLInputElement>) => {
		if ("inputType" in evt.nativeEvent && evt.nativeEvent.inputType === "insertFromPaste") {
			const parts = evt.target.value.split("/");
			const name = parts[parts.length - 1];
			if (name) {
				setValue("facebook", name);
			}
			if (!name && parts.length >= 2) {
				setValue("facebook", parts[parts.length - 2]);
			}
		} else {
			setValue("facebook", evt.target.value, { shouldDirty: true });
		}
	};

	const onConvertToBlackAndWhite = (sourceBitmap: HTMLImageElement, luminanceThreshold: number) => {
		const blackAndWhiteCanvas = document.createElement("canvas");
		blackAndWhiteCanvas.width = sourceBitmap.naturalWidth;
		blackAndWhiteCanvas.height = sourceBitmap.naturalHeight;
		const blackAndWhiteCtx = blackAndWhiteCanvas.getContext("2d") as CanvasRenderingContext2D;

		blackAndWhiteCtx?.drawImage(sourceBitmap, 0, 0);
		const imageData = blackAndWhiteCtx.getImageData(0, 0, blackAndWhiteCanvas.width, blackAndWhiteCanvas.height);
		const data = imageData.data;
		for (let i = 0; i < data.length; i += 4) {
			const r = data[i];
			const g = data[i + 1];
			const b = data[i + 2];
			const a = data[i + 3];
			if (a === 0 || (r === 255 && g === 255 && b === 255)) {
				data[i] = 255;
				data[i + 1] = 255;
				data[i + 2] = 255;
			} else if (r === 0 && g === 0 && b === 0) {
				data[i] = 0;
				data[i + 1] = 0;
				data[i + 2] = 0;
			} else {
				const luminance = 0.3 * r + 0.59 * g + 0.11 * b;
				if (luminance < luminanceThreshold) {
					data[i] = 0;
					data[i + 1] = 0;
					data[i + 2] = 0;
				} else {
					data[i] = 255;
					data[i + 1] = 255;
					data[i + 2] = 255;
				}
			}
		}
		blackAndWhiteCtx.putImageData(imageData, 0, 0);
		const blackAndWhiteImage = new Image();
		blackAndWhiteImage.crossOrigin = "anonymous";
		blackAndWhiteImage.src = blackAndWhiteCanvas.toDataURL();
		return blackAndWhiteImage;
	};

	const onChangeImageContrast = (luminanceThreshold: number) => {
		setValue("luminance_threshold", luminanceThreshold);
		const originalImage = document.getElementById("form-image") as HTMLImageElement | null;
		const newImage = document.getElementById("printout-logo") as HTMLImageElement | null;
		if (originalImage?.naturalWidth && originalImage?.naturalHeight) {
			const blackAndWhiteImage = onConvertToBlackAndWhite(originalImage, luminanceThreshold);

			if (newImage) {
				newImage.src = blackAndWhiteImage.src;
			}
		}
	};

	return (
		<div className="col-md-8 receipt-form">
			<Card>
				<Card.Body>
					<h6>{t("modules.receipt.field.bill_info.title")}</h6>
					<div className="row">
						<div className="col-md-9">
							<div className="row row-cols-1">
								<div className="col-md-12">
									<FormCheck
										type="switch"
										register={register}
										label={t("modules.receipt.action.print_organization.title")}
										name="print_organization"
										errors={errors}
									/>
								</div>
								<div className="col-md-12">
									<FormCheck
										type="switch"
										register={register}
										label={t("modules.receipt.action.print_company.title")}
										name="print_company"
										errors={errors}
									/>
								</div>
								<div className="col-md-12">
									<FormCheck
										type="switch"
										register={register}
										label={t("modules.receipt.action.print_address.title")}
										name="print_address"
										errors={errors}
									/>
								</div>
								<div className="col-md-12">
									<FormCheck
										type="switch"
										register={register}
										label={t("modules.receipt.action.print_comment.title")}
										name="print_comment"
										errors={errors}
									/>
								</div>
								<div className="col-md-12">
									<FormCheck
										type="switch"
										register={register}
										label={t("modules.receipt.action.print_employee.title")}
										name="print_employee"
										errors={errors}
									/>
								</div>
								<div className="col-md-12">
									<FormCheck
										type="switch"
										register={register}
										label={t("modules.receipt.action.print_discount.title")}
										name="print_discount"
										errors={errors}
									/>
								</div>
								<div className="col-md-12">
									<FormCheck
										type="switch"
										register={register}
										label={t("modules.receipt.action.print_qr_code.title")}
										name="print_qr_code"
										errors={errors}
									/>
								</div>
								<div className="col-md-12">
									<FormCheck
										type="switch"
										register={register}
										label={t("modules.receipt.action.print_table.title")}
										name="print_table"
										errors={errors}
									/>
								</div>
								<div className="col-12">
									<div className="d-flex flex-column mb-3">
										<Form.Label>{t("modules.receipt.action.luminance_threshold.title")}</Form.Label>
										<input
											type="range"
											min="0"
											max="255"
											step="12.75"
											onChange={(e) => onChangeImageContrast(Number(e.target.value))}
											defaultValue={defaultLuminanceThreshold}
										/>
									</div>
								</div>
							</div>
						</div>
						<div className="col-md-3 logo">
							<ImageColorForm
								label={t("common.word.printed_logo")}
								form={form}
								handleSave={handleImageChange}
								prefix="logo"
								mode="IMAGE"
								data={receipt}
								resourceLink={receiptImageData}
								resourceData={receipt.logo}
								onImageLoad={() => onChangeImageContrast(defaultLuminanceThreshold)}
							/>
						</div>
					</div>
					<FormSelectGroup
						label={t("modules.receipt.field.additions_print_method.title")}
						name="additions_print_method"
						errors={errors}
						options={[
							{
								value: PrintoutAdditionsPrintMethod.NO_ADDITIONS,
								label: t("modules.receipt.field.additions_print_method_no_additions.title"),
							},
							{
								value: PrintoutAdditionsPrintMethod.IN_DESCRIPTION,
								label: t("modules.receipt.field.additions_print_method_in_description.title"),
							},
							{
								value: PrintoutAdditionsPrintMethod.AS_SEPARATE_ITEMS,
								label: t("modules.receipt.field.additions_print_method_as_separate_items.title"),
							},
						]}
						control={form.control}
						isClearable={false}
						data-testid="additions_print_method"
					/>
					<FormSelectGroup
						control={form.control}
						name="language"
						label={t("common.word.language")}
						errors={errors}
						options={languageOptions}
						isClearable={false}
					/>
					<Form.Label>{t("modules.receipt.field.facebook.title")}</Form.Label>
					<InputGroup className="form-input-group">
						<InputGroup.Text style={{ height: "31px" }}>fb.com/</InputGroup.Text>
						<FormInput
							onChange={(evt: React.ChangeEvent<HTMLInputElement>) => onChangeFb(evt)}
							name="facebook"
							errors={errors}
							register={register}
						/>
					</InputGroup>
					<Form.Label>{t("modules.receipt.field.instagram.title")}</Form.Label>
					<InputGroup className="form-input-group">
						<InputGroup.Text style={{ height: "31px" }}>instagram.com/</InputGroup.Text>
						<FormInput
							onChange={(evt: React.ChangeEvent<HTMLInputElement>) => onChangeInsta(evt)}
							name="instagram"
							errors={errors}
							register={register}
						/>
					</InputGroup>
					<FormInput
						label={t("modules.receipt.field.website.title")}
						register={register}
						name="website"
						errors={errors}
					/>
					<FormInput
						as="textarea"
						rows={3}
						label={t("modules.receipt.field.custom_header.title")}
						register={register}
						name="custom_header"
						errors={errors}
					/>
					<FormInput
						as="textarea"
						rows={3}
						label={t("modules.receipt.field.custom_text.title")}
						register={register}
						name="custom_text"
						errors={errors}
					/>
					<FormInput
						as="textarea"
						rows={3}
						label={t("modules.receipt.field.custom_fiscal_text.title")}
						register={register}
						name="custom_fiscal_text"
						errors={errors}
					/>
				</Card.Body>
			</Card>
		</div>
	);
};

export default ReceiptForm;
